import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HealthAndSafetyRoundedIcon from "@mui/icons-material/HealthAndSafetyRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { type MouseEvent, useState } from "react";
import ReactHtmlParser from "react-html-parser";

import {
  createDefaultAdminOverviewFilters,
  filterAdminOverview,
} from "../helpers/filter-admin-overview";
import { isAppDeleterProfile } from "../helpers/get-user-role";
import {
  AdminAccessUser,
  AdminAppOverview,
  AdminClientOverview,
  AdminDirectoryUser,
  AdminOverview,
  AppHealth,
  InvitationStatus,
} from "../types/admin";
import { AppDeletionJob } from "../types/app-deletion";
import { Profile } from "../types/profile";
import { AdminOverviewFiltersToolbar } from "./admin-overview-filters";
import { BORDER, INK, MUTED, Pill, type Tone } from "./admin-ui";
import {
  AppDeletionDialog,
  type AppDeletionTarget,
  useOpenAppDeletions,
} from "./app-deletion-dialog";
import { AppIntegrityDialog } from "./app-integrity-dialog";
import { ChangeLanguageButton } from "./change-language-button";

export interface AdminPageState {
  overview?: AdminOverview;
  isLoading?: boolean;
  loadError?: string;
  userProfile?: Profile;
  language: SupportedLanguage;
  showDebugMagicLinkButton?: boolean;
}

export type AdminPageEvent =
  | "navigate-home"
  | "logout"
  | "refresh-overview"
  | "change-language"
  | "generate-magic-link-debug";

export type AdminPageEventPayload = {
  language?: SupportedLanguage;
};

// --- design tokens -------------------------------------------------------

const BRAND = "#1d9bf0";
const BRAND_LIGHT = "#52c6ff";
const BRAND_GRADIENT = `linear-gradient(135deg, ${BRAND_LIGHT} 0%, ${BRAND} 100%)`;
const PAGE_BG = "linear-gradient(180deg, #f3f6ff 0%, #e9edfb 100%)";
const CARD_SHADOW = "0 4px 20px rgba(28, 42, 92, 0.06)";
const CARD_HOVER_SHADOW = "0 10px 30px rgba(28, 42, 92, 0.12)";
const CARD_RADIUS = 3.5;

const headCellSx = {
  fontWeight: 700,
  color: MUTED,
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  borderBottom: `1px solid ${BORDER}`,
  py: 1.25,
} as const;

const cellSx = { borderBottom: `1px solid ${BORDER}`, color: INK } as const;

// --- value → tone/label mappers -----------------------------------------

const HEALTH: Record<AppHealth, { tone: Tone; label: string }> = {
  ok: { tone: "green", label: "reachable" },
  unreachable: { tone: "red", label: "unreachable" },
  unknown: { tone: "grey", label: "unknown" },
};

// Activation statuses are snake_case; their locale keys are camelCase.
const toCamelCase = (value: string) =>
  value.replace(/_(\w)/g, (_, char: string) => char.toUpperCase());

function activationTone(status: string | null): Tone {
  if (!status) return "grey";
  if (status.startsWith("active")) return "green";
  if (status === "trial") return "blue";
  if (status === "expired") return "red";
  return "grey";
}

function statusTone(status: InvitationStatus): Tone {
  return status === "pending" ? "amber" : "green";
}

function roleTone(role: string | null): Tone {
  if (role === "Owner") return "violet";
  if (role === "Admin") return "blue";
  return "grey";
}

function displayName(u: {
  firstName: string | null;
  lastName: string | null;
  email: string;
}): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return name || u.email;
}

function capitalize(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  // Date-only values (YYYY-MM-DD) as a plain local date; new Date()+toLocaleString
  // would read them as UTC midnight and shift a day in negative-offset timezones.
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso.trim())) {
    const [year, month, day] = iso.trim().split("-").map(Number);
    const date = new Date(`${iso.trim()}T00:00:00`);
    // Reject impossible calendar dates that JavaScript normalizes into next month.
    if (
      Number.isNaN(date.getTime()) ||
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return "—";
    }
    return date.toLocaleDateString();
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function clientHealth(apps: AdminAppOverview[]): { tone: Tone; label: string } {
  if (apps.length === 0) return { tone: "grey", label: "noApps" };
  if (apps.some((a) => a.health === "unreachable"))
    return { tone: "red", label: "issues" };
  if (apps.every((a) => a.health === "ok"))
    return { tone: "green", label: "healthy" };
  if (apps.every((a) => a.health === "unknown"))
    return { tone: "grey", label: "unknown" };
  return { tone: "amber", label: "partial" };
}

function initials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  const chars = (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
  return (chars || label[0] || "?").toUpperCase();
}

// --- building blocks -----------------------------------------------------

function SectionTitle({
  children,
  count,
}: {
  children: React.ReactNode;
  count?: number;
}) {
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1, mt: 3, mb: 1.25 }}
    >
      <Box
        sx={{
          width: 4,
          height: 15,
          borderRadius: 1,
          background: BRAND_GRADIENT,
        }}
      />
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, color: "#33405e", letterSpacing: "0.01em" }}
      >
        {children}
      </Typography>
      {count != null && (
        <Typography
          variant="caption"
          sx={{
            color: MUTED,
            bgcolor: "#eef1f8",
            px: 0.9,
            py: 0.1,
            borderRadius: 999,
            fontWeight: 700,
          }}
        >
          {count}
        </Typography>
      )}
    </Box>
  );
}

const nestedCardSx = {
  borderRadius: 2.5,
  border: `1px solid ${BORDER}`,
  overflow: "hidden",
  bgcolor: "#fff",
} as const;

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" sx={{ color: "#9aa3b8", py: 0.5 }}>
      {children}
    </Typography>
  );
}

interface DeletionControls {
  canDelete: boolean;
  jobsByApp: Record<string, AppDeletionJob>;
  open: (target: AppDeletionTarget) => void;
}

function AppDateCell({ value }: { value: string | null }) {
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value?.trim() ?? "");
  // Validate the source calendar date before parsing can normalize it or shift
  // it into a different day in the browser's timezone.
  const sourceDate = value?.trim().match(/^(\d{4}-\d{2}-\d{2})(?:T|$)/)?.[1];
  const date =
    !value || (sourceDate && formatDate(sourceDate) === "—")
      ? null
      : new Date(isDateOnly ? `${value.trim()}T00:00:00` : value);

  return (
    <TableCell>
      {date && !Number.isNaN(date.getTime()) ? (
        <>
          <Typography component="span" variant="body2" display="block">
            {date.toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </Typography>
          {/\d{2}:\d{2}/.test(value ?? "") && (
            <Typography
              component="span"
              variant="caption"
              display="block"
              color="text.secondary"
            >
              {date.toLocaleTimeString(undefined, {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Typography>
          )}
        </>
      ) : (
        "—"
      )}
    </TableCell>
  );
}

/** One app row inside a client, expandable to reveal its access list. */
function AppRow({
  app,
  clientName,
  deletion,
  language,
}: {
  app: AdminAppOverview;
  clientName: string;
  deletion: DeletionControls;
  language: SupportedLanguage;
}) {
  const deletionJob = deletion.jobsByApp[app.id];
  const openDeletion = (e: MouseEvent) => {
    e.stopPropagation();
    deletion.open({ appId: app.id, appName: app.name, clientName });
  };
  const [open, setOpen] = useState(false);
  const [integrityOpen, setIntegrityOpen] = useState(false);
  const health = HEALTH[app.health];
  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen((v) => !v)}
        sx={{ cursor: "pointer", "& td": cellSx }}
      >
        <TableCell sx={{ width: 44 }}>
          <IconButton size="small">
            {open ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600, color: INK }}>{app.name}</TableCell>
        <TableCell>
          <Pill
            tone={activationTone(app.activationStatus)}
            label={
              app.activationStatus
                ? getLocalizedMessage(
                    language,
                    `adminPanel.appsAndHealth.activation.${toCamelCase(app.activationStatus)}`
                  )
                : "—"
            }
          />
        </TableCell>
        <TableCell>
          <Pill
            tone={health.tone}
            label={getLocalizedMessage(
              language,
              `adminPanel.appsAndHealth.health.${health.label}`
            )}
            dot
          />
        </TableCell>
        <AppDateCell value={app.createdAt} />
        <AppDateCell value={app.trialExpiresAt} />
        <TableCell>
          <Pill
            tone="grey"
            label={getLocalizedMessage(
              language,
              "adminPanel.appsAndHealth.table.xUsers",
              { count: app.accessUserCount }
            )}
          />
        </TableCell>
        <TableCell>
          {app.url ? (
            <Tooltip title={app.url} placement="top" describeChild>
              <Typography
                component="a"
                href={app.url}
                target="_blank"
                rel="noreferrer"
                aria-label={getLocalizedMessage(
                  language,
                  "adminPanel.appsAndHealth.table.openAppAriaLabel",
                  { appName: app.name }
                )}
                onClick={(e) => e.stopPropagation()}
                variant="caption"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  whiteSpace: "nowrap",
                  color: BRAND,
                  textDecoration: "none",
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                  "&:focus-visible": {
                    outline: "2px solid currentColor",
                    outlineOffset: 2,
                    borderRadius: 0.5,
                  },
                }}
              >
                {getLocalizedMessage(
                  language,
                  "adminPanel.appsAndHealth.table.openApp"
                )}
                <OpenInNewRoundedIcon
                  aria-hidden="true"
                  sx={{ fontSize: "1em" }}
                />
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="caption" sx={{ color: "#9aa3b8" }}>
              —
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ whiteSpace: "nowrap" }}
          >
            <Button
              size="small"
              variant="text"
              startIcon={<HealthAndSafetyRoundedIcon />}
              onClick={(e) => {
                e.stopPropagation();
                setIntegrityOpen(true);
              }}
              sx={{ textTransform: "none" }}
            >
              {getLocalizedMessage(
                language,
                "adminPanel.appsAndHealth.table.check"
              )}
            </Button>
            {deletionJob ? (
              <Box
                component="span"
                onClick={openDeletion}
                sx={{ cursor: "pointer" }}
              >
                <Pill
                  tone={deletionJob.status === "failed" ? "red" : "amber"}
                  label={getLocalizedMessage(
                    language,
                    `appDeletionDialog.rowStatus.${deletionJob.status}`
                  )}
                  dot
                />
              </Box>
            ) : (
              deletion.canDelete && (
                <IconButton
                  size="small"
                  color="error"
                  aria-label={getLocalizedMessage(
                    language,
                    "appDeletionDialog.deleteApp"
                  )}
                  title={getLocalizedMessage(
                    language,
                    "appDeletionDialog.deleteApp"
                  )}
                  onClick={openDeletion}
                >
                  <DeleteForeverRoundedIcon fontSize="small" />
                </IconButton>
              )
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ p: 0, border: 0 }} colSpan={9}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ px: 4, py: 1.5, bgcolor: "#fbfcff" }}>
              <SectionTitle count={app.accessUserCount}>
                {getLocalizedMessage(
                  language,
                  "adminPanel.appsAndHealth.table.whoCanSignIn"
                )}
              </SectionTitle>
              <Typography
                variant="caption"
                sx={{ color: MUTED, display: "block", mb: 1, mt: -0.5 }}
              >
                {getLocalizedMessage(
                  language,
                  "adminPanel.appsAndHealth.table.whoCanSignInDescription"
                )}
              </Typography>
              <AccessTable users={app.accessUsers} language={language} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <AppIntegrityDialog
        appId={app.id}
        appName={app.name}
        open={integrityOpen}
        onClose={() => setIntegrityOpen(false)}
        language={language}
      />
    </>
  );
}

function AccessTable({
  users,
  language,
}: {
  users: AdminAccessUser[];
  language: SupportedLanguage;
}) {
  if (users.length === 0) {
    return (
      <EmptyHint>
        {getLocalizedMessage(
          language,
          "adminPanel.appsAndHealth.accessTable.noUsers"
        )}
      </EmptyHint>
    );
  }
  return (
    <TableContainer component={Paper} elevation={0} sx={nestedCardSx}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#f7f9ff" }}>
            <TableCell sx={headCellSx}>
              {" "}
              {getLocalizedMessage(
                language,
                "adminPanel.appsAndHealth.accessTable.user"
              )}
            </TableCell>
            <TableCell sx={headCellSx}>
              {" "}
              {getLocalizedMessage(
                language,
                "adminPanel.appsAndHealth.accessTable.email"
              )}
            </TableCell>
            <TableCell sx={headCellSx}>
              {" "}
              {getLocalizedMessage(
                language,
                "adminPanel.appsAndHealth.accessTable.appRole"
              )}
            </TableCell>
            <TableCell sx={headCellSx}>
              {" "}
              {getLocalizedMessage(
                language,
                "adminPanel.appsAndHealth.accessTable.accountRole"
              )}
            </TableCell>
            <TableCell sx={headCellSx}>
              {" "}
              {getLocalizedMessage(
                language,
                "adminPanel.appsAndHealth.accessTable.status"
              )}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((u) => (
            <TableRow key={`${u.id ?? "noid"}:${u.email}`} hover>
              <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {displayName(u)}
                  {u.accessVia === "creator" && (
                    <Pill
                      tone="amber"
                      label={getLocalizedMessage(
                        language,
                        "adminPanel.appsAndHealth.accessTable.creator"
                      )}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ ...cellSx, color: MUTED }}>{u.email}</TableCell>
              <TableCell sx={cellSx}>
                {u.appRole ? (
                  <Pill tone="violet" label={capitalize(u.appRole)} />
                ) : (
                  <Typography variant="caption" sx={{ color: "#9aa3b8" }}>
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={cellSx}>
                {u.role ? <Pill tone={roleTone(u.role)} label={u.role} /> : "—"}
              </TableCell>
              <TableCell sx={cellSx}>
                <Pill
                  tone={statusTone(u.status)}
                  label={getLocalizedMessage(
                    language,
                    `adminPanel.appsAndHealth.accessTable.${u.status === "pending" ? "pending" : "active"}`
                  )}
                  dot
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function InvitationsTable({
  invitations,
  language,
}: {
  invitations: AdminDirectoryUser[];
  language: SupportedLanguage;
}) {
  if (invitations.length === 0) {
    return (
      <EmptyHint>
        {" "}
        {getLocalizedMessage(
          language,
          "adminPanel.pendingInvitations.table.noPendingInvitations"
        )}
      </EmptyHint>
    );
  }
  return (
    <TableContainer component={Paper} elevation={0} sx={nestedCardSx}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#f7f9ff" }}>
            <TableCell sx={headCellSx}>
              {getLocalizedMessage(
                language,
                "adminPanel.pendingInvitations.table.email"
              )}
            </TableCell>
            <TableCell sx={headCellSx}>
              {getLocalizedMessage(
                language,
                "adminPanel.pendingInvitations.table.accountRole"
              )}
            </TableCell>
            <TableCell sx={headCellSx}>
              {getLocalizedMessage(
                language,
                "adminPanel.pendingInvitations.table.invitedVia"
              )}
            </TableCell>
            <TableCell sx={headCellSx}>
              {getLocalizedMessage(
                language,
                "adminPanel.pendingInvitations.table.invitedBy"
              )}
            </TableCell>
            <TableCell sx={headCellSx}>
              {getLocalizedMessage(
                language,
                "adminPanel.pendingInvitations.table.invitedAt"
              )}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invitations.map((u) => (
            <TableRow key={u.id} hover>
              <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                {u.email}
              </TableCell>
              <TableCell sx={cellSx}>
                <Pill tone={roleTone(u.role)} label={u.role} />
              </TableCell>
              <TableCell sx={cellSx}>
                {u.invitedToTool ? (
                  <Pill tone="violet" label={u.invitedToTool} />
                ) : (
                  <Pill
                    tone="grey"
                    label={getLocalizedMessage(
                      language,
                      "adminPanel.pendingInvitations.table.homePortal"
                    )}
                  />
                )}
              </TableCell>
              <TableCell sx={{ ...cellSx, color: MUTED }}>
                {u.inviterName ?? "—"}
              </TableCell>
              <TableCell sx={{ ...cellSx, color: MUTED }}>
                {formatDate(u.invitedAt)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DirectoryTable({
  users,
  language,
}: {
  users: AdminDirectoryUser[];
  language: SupportedLanguage;
}) {
  if (users.length === 0) {
    return (
      <EmptyHint>
        {getLocalizedMessage(
          language,
          "adminPanel.accountDirectory.table.noUsers"
        )}
      </EmptyHint>
    );
  }
  return (
    <TableContainer component={Paper} elevation={0} sx={nestedCardSx}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#f7f9ff" }}>
            <TableCell sx={headCellSx}>
              {getLocalizedMessage(
                language,
                "adminPanel.accountDirectory.table.user"
              )}
            </TableCell>
            <TableCell sx={headCellSx}>
              {getLocalizedMessage(
                language,
                "adminPanel.accountDirectory.table.email"
              )}
            </TableCell>
            <TableCell sx={headCellSx}>
              {getLocalizedMessage(
                language,
                "adminPanel.accountDirectory.table.role"
              )}
            </TableCell>
            <TableCell sx={headCellSx}>
              {getLocalizedMessage(
                language,
                "adminPanel.accountDirectory.table.status"
              )}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.id} hover>
              <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                {displayName(u)}
              </TableCell>
              <TableCell sx={{ ...cellSx, color: MUTED }}>{u.email}</TableCell>
              <TableCell sx={cellSx}>
                <Pill tone={roleTone(u.role)} label={u.role} />
              </TableCell>
              <TableCell sx={cellSx}>
                <Pill
                  tone={statusTone(u.status)}
                  label={getLocalizedMessage(
                    language,
                    `adminPanel.accountDirectory.table.${u.status === "pending" ? "pending" : "active"}`
                  )}
                  dot
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function LimitsPanel({
  client,
  language,
}: {
  client: AdminClientOverview;
  language: SupportedLanguage;
}) {
  const { limits, seatUsage } = client;
  const rows: Array<[string, string]> = [
    [
      getLocalizedMessage(language, "adminPanel.plansAndLimits.plan"),
      limits.planName ??
        getLocalizedMessage(
          language,
          "adminPanel.plansAndLimits.noPlanFreeTier"
        ),
    ],
    [
      getLocalizedMessage(language, "adminPanel.plansAndLimits.basePrice"),
      limits.basePriceEur != null
        ? getLocalizedMessage(
            language,
            "adminPanel.plansAndLimits.pricePerMonth",
            { basePrice: limits.basePriceEur }
          )
        : "—",
    ],
    [
      getLocalizedMessage(language, "adminPanel.plansAndLimits.includedTools"),
      String(limits.includedTools),
    ],
    [
      getLocalizedMessage(language, "adminPanel.plansAndLimits.userLimit"),
      String(limits.userLimit),
    ],
    [
      getLocalizedMessage(language, "adminPanel.plansAndLimits.accountMembers"),
      `${seatUsage.directoryUserCount} / ${seatUsage.userLimit}`,
    ],
    [
      getLocalizedMessage(language, "adminPanel.plansAndLimits.seatsInUse"),
      seatUsage.seatUserCount == null
        ? getLocalizedMessage(language, "adminPanel.plansAndLimits.unavailable")
        : String(seatUsage.seatUserCount),
    ],
    [
      getLocalizedMessage(
        language,
        "adminPanel.plansAndLimits.remainingInvites"
      ),
      String(seatUsage.remainingInvites),
    ],
  ];
  return (
    <TableContainer component={Paper} elevation={0} sx={nestedCardSx}>
      <Table size="small">
        <TableBody>
          {rows.map(([label, value]) => (
            <TableRow key={label} hover>
              <TableCell sx={{ ...cellSx, width: "45%", color: MUTED }}>
                {label}
              </TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** One collapsible client row. */
function ClientRow({
  client,
  visibleApps,
  deletion,
  language,
}: {
  client: AdminClientOverview;
  visibleApps: AdminAppOverview[];
  deletion: DeletionControls;
  language: SupportedLanguage;
}) {
  const [open, setOpen] = useState(false);
  const health = clientHealth(client.apps);
  const pending = client.invitations.length;

  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen((v) => !v)}
        sx={{
          cursor: "pointer",
          transition: "background-color 0.15s ease",
          "& td": { borderBottom: `1px solid ${BORDER}`, py: 1.75 },
          ...(open && { bgcolor: "#f3f7ff" }),
        }}
      >
        <TableCell sx={{ width: 52 }}>
          <IconButton size="small">
            {open ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                background: BRAND_GRADIENT,
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.8rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 4px 10px rgba(29,155,240,0.3)",
              }}
            >
              {initials(client.companyName || client.name)}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: INK }}>
                {client.name}
              </Typography>
              {client.companyName && (
                <Typography variant="caption" sx={{ color: MUTED }}>
                  {client.companyName}
                </Typography>
              )}
            </Box>
          </Box>
        </TableCell>
        <AppDateCell value={client.createdAt} />
        <TableCell>
          {client.limits.planName ? (
            <Pill tone="blue" label={client.limits.planName} />
          ) : (
            <Typography variant="body2" sx={{ color: "#9aa3b8" }}>
              {getLocalizedMessage(language, "adminPanel.overview.noPlan")}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ fontWeight: 600, color: INK }}>
          {client.seatUsage.directoryUserCount}
          <Box component="span" sx={{ color: "#aab2c5", fontWeight: 500 }}>
            {" "}
            / {client.seatUsage.userLimit}
          </Box>
        </TableCell>
        <TableCell sx={{ color: MUTED }}>
          {client.seatUsage.seatUserCount ?? "n/a"}
        </TableCell>
        <TableCell sx={{ fontWeight: 600, color: INK }}>
          {client.appCount}
        </TableCell>
        <TableCell>
          <Pill
            tone={health.tone}
            label={getLocalizedMessage(
              language,
              `adminPanel.appsAndHealth.health.${health.label}`
            )}
            dot
          />
        </TableCell>
        <TableCell>
          {pending > 0 ? (
            <Pill
              tone="amber"
              label={getLocalizedMessage(
                language,
                "adminPanel.overview.xPending",
                { count: pending }
              )}
              dot
            />
          ) : (
            <Typography sx={{ color: "#c2c8d6" }}>—</Typography>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ p: 0, border: 0 }} colSpan={9}>
          <Collapse in={open} unmountOnExit>
            <Box
              sx={{
                px: 3.5,
                pb: 3,
                pt: 0.5,
                bgcolor: "#f6f8ff",
                borderLeft: `3px solid ${BRAND_LIGHT}`,
              }}
            >
              <SectionTitle count={client.appCount}>
                {getLocalizedMessage(
                  language,
                  "adminPanel.appsAndHealth.title"
                )}
              </SectionTitle>
              <Typography
                variant="caption"
                sx={{ color: MUTED, display: "block", mb: 1, mt: -0.5 }}
              >
                {getLocalizedMessage(
                  language,
                  "adminPanel.appsAndHealth.description"
                )}
              </Typography>
              {visibleApps.length < client.apps.length && (
                <Typography
                  variant="caption"
                  sx={{ color: MUTED, display: "block", mb: 1 }}
                >
                  {getLocalizedMessage(
                    language,
                    "adminPanel.filters.appsShown",
                    { shown: visibleApps.length, total: client.appCount }
                  )}
                </Typography>
              )}
              {client.apps.length === 0 ? (
                <EmptyHint>
                  {" "}
                  {getLocalizedMessage(language, "adminPanel.noApps")}
                </EmptyHint>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={nestedCardSx}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f7f9ff" }}>
                        <TableCell sx={headCellSx} />
                        <TableCell sx={headCellSx}>
                          {getLocalizedMessage(
                            language,
                            "adminPanel.appsAndHealth.table.app"
                          )}
                        </TableCell>
                        <TableCell sx={headCellSx}>
                          {getLocalizedMessage(
                            language,
                            "adminPanel.appsAndHealth.table.status"
                          )}
                        </TableCell>
                        <TableCell sx={headCellSx}>
                          {getLocalizedMessage(
                            language,
                            "adminPanel.appsAndHealth.table.health"
                          )}
                        </TableCell>
                        <TableCell sx={headCellSx}>
                          {getLocalizedMessage(
                            language,
                            "adminPanel.appsAndHealth.table.createdAt"
                          )}
                        </TableCell>
                        <TableCell sx={headCellSx}>
                          {getLocalizedMessage(
                            language,
                            "adminPanel.appsAndHealth.table.trialExpires"
                          )}
                        </TableCell>
                        <TableCell sx={headCellSx}>
                          {getLocalizedMessage(
                            language,
                            "adminPanel.appsAndHealth.table.access"
                          )}
                        </TableCell>
                        <TableCell sx={headCellSx}>
                          {getLocalizedMessage(
                            language,
                            "adminPanel.appsAndHealth.table.url"
                          )}
                        </TableCell>
                        <TableCell sx={headCellSx}>
                          {getLocalizedMessage(
                            language,
                            "adminPanel.appsAndHealth.table.actions"
                          )}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleApps.map((app) => (
                        <AppRow
                          key={app.id}
                          app={app}
                          clientName={client.name}
                          deletion={deletion}
                          language={language}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <SectionTitle count={pending}>
                {" "}
                {getLocalizedMessage(
                  language,
                  "adminPanel.pendingInvitations.title"
                )}
              </SectionTitle>
              <Typography
                variant="caption"
                sx={{ color: MUTED, display: "block", mb: 1, mt: -0.5 }}
              >
                {getLocalizedMessage(
                  language,
                  "adminPanel.pendingInvitations.description"
                )}
              </Typography>
              <InvitationsTable
                invitations={client.invitations}
                language={language}
              />

              <SectionTitle count={client.users.length}>
                {getLocalizedMessage(
                  language,
                  "adminPanel.accountDirectory.title"
                )}
              </SectionTitle>
              <DirectoryTable users={client.users} language={language} />

              <SectionTitle>
                {" "}
                {getLocalizedMessage(
                  language,
                  "adminPanel.plansAndLimits.title"
                )}
              </SectionTitle>
              <LimitsPanel client={client} language={language} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function SummaryCard({
  label,
  value,
  icon,
  accent,
  tint,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: string;
  tint: string;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: "1 1 200px",
        px: 3,
        py: 2.75,
        borderRadius: CARD_RADIUS,
        boxShadow: CARD_SHADOW,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: CARD_HOVER_SHADOW,
        },
      }}
    >
      <Box>
        <Typography
          sx={{
            fontWeight: 800,
            color: INK,
            fontSize: "2rem",
            lineHeight: 1.1,
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: MUTED, fontWeight: 600, letterSpacing: "0.01em" }}
        >
          {label}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: 3,
          bgcolor: tint,
          color: accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          "& svg": { fontSize: 26 },
        }}
      >
        {icon}
      </Box>
    </Paper>
  );
}

const headerButtonSx = {
  textTransform: "none",
  borderRadius: 2.5,
  fontWeight: 600,
  px: 2,
} as const;

export const AdminPage: PimoReactComponent<
  AdminPageState,
  AdminPageEvent,
  AdminPageEventPayload
> = ({
  fireEvent,
  overview,
  language,
  isLoading = false,
  loadError,
  userProfile,
  showDebugMagicLinkButton = false,
}) => {
  const [filters, setFilters] = useState(createDefaultAdminOverviewFilters);
  const canDelete = isAppDeleterProfile(userProfile);
  const deletions = useOpenAppDeletions(canDelete, () =>
    fireEvent?.("refresh-overview", undefined)
  );
  const [deleteTarget, setDeleteTarget] = useState<AppDeletionTarget | null>(
    null
  );
  const deletion: DeletionControls = {
    canDelete,
    jobsByApp: deletions.jobsByApp,
    open: setDeleteTarget,
  };
  const clients = overview?.clients ?? [];
  const rows = filterAdminOverview(clients, filters);
  const totalApps = clients.reduce((sum, c) => sum + c.appCount, 0);
  const totalPending = clients.reduce(
    (sum, c) => sum + c.invitations.length,
    0
  );
  const appsUnreachable = clients.reduce(
    (sum, c) => sum + c.apps.filter((a) => a.health === "unreachable").length,
    0
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: PAGE_BG,
        p: { xs: 2, md: 4 },
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ maxWidth: 1320, minWidth: 0, width: "100%", mx: "auto" }}>
        {/* Header card */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: CARD_RADIUS,
            boxShadow: CARD_SHADOW,
            px: 3,
            py: 2.25,
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <img src="/pimo.png" alt="Pimo" style={{ height: 36 }} />
            <Box sx={{ width: "1px", height: 34, bgcolor: BORDER }} />
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  fontSize: "1.4rem",
                  background: BRAND_GRADIENT,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {getLocalizedMessage(language, "adminPanel.title")}
              </Typography>
              {overview && (
                <Typography variant="caption" sx={{ color: MUTED }}>
                  {ReactHtmlParser(
                    getLocalizedMessage(language, "adminPanel.subTitle", {
                      clientCount: overview.clientCount,
                      generatedAt: formatDate(overview.generatedAt),
                    })
                  )}
                </Typography>
              )}
            </Box>
          </Box>
          <Stack direction="row" spacing={1.25}>
            <ChangeLanguageButton
              language={language}
              onLanguageChange={(newLanguage) =>
                fireEvent?.("change-language", { language: newLanguage })
              }
            />
            {showDebugMagicLinkButton && (
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  fireEvent?.("generate-magic-link-debug", undefined)
                }
                sx={{
                  ...headerButtonSx,
                  borderColor: "#d9deec",
                  color: "#475069",
                }}
              >
                Generate magic link (debug)
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshRoundedIcon />}
              onClick={() => fireEvent?.("refresh-overview", undefined)}
              sx={{
                ...headerButtonSx,
                borderColor: "#d9deec",
                color: "#475069",
              }}
            >
              {getLocalizedMessage(language, "dialogActions.refresh")}
            </Button>
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={() => fireEvent?.("navigate-home", undefined)}
              sx={{
                ...headerButtonSx,
                background: BRAND_GRADIENT,
                boxShadow: "0 4px 12px rgba(29,155,240,0.3)",
              }}
            >
              {getLocalizedMessage(language, "dialogActions.backToPortal")}
            </Button>
            <Button
              variant="text"
              size="small"
              startIcon={<LogoutRoundedIcon />}
              onClick={() => fireEvent?.("logout", undefined)}
              sx={{ ...headerButtonSx, color: MUTED }}
            >
              {getLocalizedMessage(language, "dialogActions.logout")}
            </Button>
          </Stack>
        </Paper>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
            <CircularProgress sx={{ color: BRAND }} />
          </Box>
        ) : loadError ? (
          <Paper
            elevation={0}
            sx={{ p: 4, borderRadius: CARD_RADIUS, boxShadow: CARD_SHADOW }}
          >
            <Typography color="error">{loadError}</Typography>
          </Paper>
        ) : (
          <>
            {overview?.keycloakReachable === false && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: CARD_RADIUS,
                  boxShadow: CARD_SHADOW,
                  borderLeft: "4px solid #e6a700",
                }}
              >
                <Typography
                  sx={{ color: INK, fontWeight: 600, fontSize: "0.9rem" }}
                >
                  {getLocalizedMessage(
                    language,
                    "adminPanel.keycloakUnreachable"
                  )}
                </Typography>
                <Typography variant="caption" sx={{ color: MUTED }}>
                  {getLocalizedMessage(language, "adminPanel.incompleteData")}
                </Typography>
              </Paper>
            )}
            <Stack
              direction="row"
              spacing={0}
              sx={{ mb: 3, flexWrap: "wrap", gap: 2.5 }}
            >
              <SummaryCard
                label={getLocalizedMessage(
                  language,
                  "adminPanel.summaryCards.clients"
                )}
                value={clients.length}
                icon={<BusinessRoundedIcon />}
                accent={BRAND}
                tint="rgba(29,155,240,0.12)"
              />
              <SummaryCard
                label={getLocalizedMessage(
                  language,
                  "adminPanel.summaryCards.apps"
                )}
                value={totalApps}
                icon={<AppsRoundedIcon />}
                accent="#7c4dff"
                tint="rgba(124,77,255,0.12)"
              />
              <SummaryCard
                label={getLocalizedMessage(
                  language,
                  "adminPanel.summaryCards.pendingInvites"
                )}
                value={totalPending}
                icon={<MailOutlineRoundedIcon />}
                accent="#e08a00"
                tint="rgba(224,138,0,0.14)"
              />
              <SummaryCard
                label={getLocalizedMessage(
                  language,
                  "adminPanel.summaryCards.unreachableApps"
                )}
                value={appsUnreachable}
                icon={<WarningAmberRoundedIcon />}
                accent={appsUnreachable > 0 ? "#e53935" : "#2e9e5b"}
                tint={
                  appsUnreachable > 0
                    ? "rgba(229,57,53,0.12)"
                    : "rgba(46,158,91,0.12)"
                }
              />
            </Stack>

            <Box sx={{ mb: 3, minWidth: 0 }}>
              <AdminOverviewFiltersToolbar
                filters={filters}
                language={language}
                totalClients={clients.length}
                matchedClients={rows.length}
                fireEvent={(_event, nextFilters) => {
                  if (nextFilters) setFilters(nextFilters);
                }}
              />
            </Box>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: CARD_RADIUS,
                boxShadow: CARD_SHADOW,
                minWidth: 0,
                overflowX: "auto",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f6f8ff" }}>
                    <TableCell sx={headCellSx} />
                    <TableCell sx={headCellSx}>
                      {getLocalizedMessage(
                        language,
                        "adminPanel.overview.client"
                      )}
                    </TableCell>
                    <TableCell sx={headCellSx}>
                      {getLocalizedMessage(
                        language,
                        "adminPanel.overview.createdAt"
                      )}
                    </TableCell>
                    <TableCell sx={headCellSx}>
                      {getLocalizedMessage(
                        language,
                        "adminPanel.overview.plan"
                      )}
                    </TableCell>
                    <TableCell sx={headCellSx}>
                      {getLocalizedMessage(
                        language,
                        "adminPanel.overview.membersLimit"
                      )}
                    </TableCell>
                    <TableCell sx={headCellSx}>
                      {getLocalizedMessage(
                        language,
                        "adminPanel.overview.seats"
                      )}
                    </TableCell>
                    <TableCell sx={headCellSx}>
                      {getLocalizedMessage(
                        language,
                        "adminPanel.overview.apps"
                      )}
                    </TableCell>
                    <TableCell sx={headCellSx}>
                      {getLocalizedMessage(
                        language,
                        "adminPanel.overview.health"
                      )}
                    </TableCell>
                    <TableCell sx={headCellSx}>
                      {getLocalizedMessage(
                        language,
                        "adminPanel.overview.invites"
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        sx={{ textAlign: "center", py: 8 }}
                      >
                        <Typography variant="body2" sx={{ color: "#9aa3b8" }}>
                          {getLocalizedMessage(
                            language,
                            clients.length === 0
                              ? "adminPanel.noClientsFound"
                              : "adminPanel.filters.noResults"
                          )}
                        </Typography>
                        {clients.length > 0 && (
                          <Button
                            onClick={() =>
                              setFilters(createDefaultAdminOverviewFilters())
                            }
                          >
                            {getLocalizedMessage(
                              language,
                              "adminPanel.filters.reset"
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map(({ client, visibleApps }) => (
                      <ClientRow
                        key={client.id}
                        client={client}
                        visibleApps={visibleApps}
                        deletion={deletion}
                        language={language}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
      <AppDeletionDialog
        target={deleteTarget}
        openJob={
          deleteTarget ? deletions.jobsByApp[deleteTarget.appId] : undefined
        }
        onClose={() => setDeleteTarget(null)}
        onJobChanged={() => void deletions.refresh()}
        language={language}
      />
    </Box>
  );
};
